import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Calendar, AlertTriangle, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { GoogleCalendarDialog } from "@/components/integrations/GoogleCalendarDialog";
import { cn } from "@/lib/utils";

export interface GoogleCalendarAgendaReconnectAlertProps {
  needsReconnect: boolean;
  hasConnection: boolean;
  isChecking: boolean;
  refresh: () => void | Promise<void>;
  className?: string;
}

export function GoogleCalendarAgendaReconnectAlert({
  needsReconnect,
  hasConnection,
  isChecking,
  refresh,
  className,
}: GoogleCalendarAgendaReconnectAlertProps) {
  const [dismissed, setDismissed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const prevNeedsReconnect = useRef(false);

  useEffect(() => {
    if (needsReconnect && !prevNeedsReconnect.current) {
      setDismissed(false);
    }
    prevNeedsReconnect.current = needsReconnect;
  }, [needsReconnect]);

  if (isChecking || !hasConnection || !needsReconnect || dismissed) {
    return null;
  }

  return (
    <>
      <Alert
        className={cn(
          "relative border-amber-300/80 bg-amber-50 text-amber-950 dark:border-amber-700/80 dark:bg-amber-950/40 dark:text-amber-50 pr-12 pl-12",
          className,
        )}
      >
        <div
          className="absolute left-4 top-4 flex items-center gap-1 text-amber-700 dark:text-amber-300"
          aria-hidden
        >
          <Calendar className="h-4 w-4 shrink-0" />
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
        </div>
        <AlertTitle className="text-amber-950 dark:text-amber-50 leading-tight">
          Sincronização com o Google Calendar pausada
        </AlertTitle>
        <AlertDescription className="text-amber-900/90 dark:text-amber-100/90 mt-2 space-y-3">
          <p>
            A conexão com a sua conta Google expirou. Reconecte para criar e atualizar eventos na agenda com
            segurança.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="bg-amber-700 text-white hover:bg-amber-800 dark:bg-amber-600 dark:hover:bg-amber-500"
              onClick={() => setDialogOpen(true)}
            >
              Reconectar conta Google
            </Button>
            <Button variant="ghost" size="sm" className="text-amber-900 dark:text-amber-100" asChild>
              <Link to="/integrations">Abrir Integrações</Link>
            </Button>
          </div>
        </AlertDescription>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2 h-8 w-8 text-amber-900 hover:bg-amber-200/80 dark:text-amber-100 dark:hover:bg-amber-900/50"
          aria-label="Fechar aviso"
          onClick={() => setDismissed(true)}
        >
          <X className="h-4 w-4" />
        </Button>
      </Alert>

      <GoogleCalendarDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) void refresh();
        }}
      />
    </>
  );
}
